// Alguns sub-campos podem não existir na resposta real do backend ainda
// (ver cmaCardsAdapter.js — ex.: winding2/winding3/ambient, frequency) —
// filtra qualquer linha cujo valor não tenha vindo, em vez de mostrar
// "undefined"/"null" na tela.
function semValorAusente(rows) {
  return rows.filter(row => row.value !== null && row.value !== undefined);
}

// As linhas de fase são o mapeamento fixo de sempre; `extraRows` (ver
// cmaCardsAdapter.js, buildBushings) são campos do módulo BM fora desse
// mapeamento — já vêm prontas no padrão dinâmico dos outros cards, só
// anexadas ao final.
function buildBushingsRows(readings) {
  const phaseRows = semValorAusente(readings.bushings.phases.flatMap(phase => [
    {
      labelKey: 'cma.fields.capacitance', labelParams: { phase: phase.phase }, value: phase.capacitance, unit: 'pF',
    },
    {
      labelKey: 'cma.fields.tangent-delta', labelParams: { phase: phase.phase }, value: phase.tangentDelta, unit: '%',
    },
    {
      labelKey: 'cma.fields.leakage-current', labelParams: { phase: phase.phase }, value: phase.leakageCurrent, unit: 'mA',
    },
  ]));
  return [...phaseRows, ...readings.bushings.extraRows];
}

// Gás/Umidade, Temperaturas, Tensão na Carga, Comutador e Dados Elétricos já
// chegam de cmaCardsAdapter.js com as linhas prontas (`rows`) — `nome`,
// `valor`/`unidadeMedida` do backend, sem mapa fixo de rótulo por campo (ver
// cabeçalho do adapter). Só repassa; nada aqui sabe mais quantos campos cada
// um tem.
function buildDynamicRowsOf(statusKey) {
  return readings => (readings[statusKey] ? readings[statusKey].rows : []);
}

// A linha de ventiladores fica com o par bruto (ativos/total) em labelParams
// — a composição em texto ("3 de 3 ativos") depende de tradução e é feita em
// CmaReadingCard.vue (groupCoolingRows), não aqui, que é dado puro.
function buildCoolingRows(readings) {
  const { cooling } = readings;
  return semValorAusente([
    {
      labelKey: 'cma.fields.cooling-stage', labelParams: {}, value: cooling.stage, unit: '',
    },
    {
      labelKey: 'cma.fields.fans',
      labelParams: { active: cooling.activeFans, total: cooling.totalFans },
      value: `${cooling.activeFans}/${cooling.totalFans}`,
      unit: '',
    },
  ]);
}

// `statusKey` é a chave (camelCase) usada em readings.cardStatuses — não dá
// para derivar do `id` (kebab-case, usado em CSS/i18n) sem essa tabela, já
// que os 2 formatos divergem para ids compostos (ex.: "gas-and-humidity").
//
// `id` aqui é a posição fixa na tela (classe CSS/ref — ver &__card--* em
// CmaDashboard.vue) e a ORDEM do array é a numeração exibida no título de
// cada card ("1. Buchas", "2. Comutador", ...). Essa ordem segue a leitura
// visual da tela — coluna esquerda de cima a baixo (buchas, gás/umidade,
// temperaturas, tensão na carga) e depois a direita, também de cima a baixo
// (bolsa, comutador, dados elétricos, resfriamento) —, não o conteúdo (ver
// CONTENT_OVERRIDES logo abaixo, quem decide qual leitura aparece em cada
// posição). Por isso a numeração sai contínua na tela: 1 a 4 descendo a
// coluna esquerda, 5 a 8 descendo a direita.
// `icon` era classe do FontAwesome (ex.: "fa-charging-station"), mas nunca
// chegou a ser renderizado em lugar nenhum — CmaReadingCard.vue não tinha
// nenhum <i>/ícone no cabeçalho do card. Agora é o caminho de um SVG próprio
// (ver public/static/imgs/cma-icons/), exibido à esquerda do título (ver
// CmaReadingCard.vue, &__icon) — `null` para os cards que ainda não têm um
// ícone definitivo (o card simplesmente não mostra nada ali, ver `v-if`).
const CARD_BUILDERS = [
  {
    id: 'bushings', statusKey: 'bushings', titleKey: 'cma.cards.bushings.title', icon: '/static/imgs/cma-icons/bushings.svg', buildRows: buildBushingsRows,
  },
  {
    id: 'gas-and-humidity', statusKey: 'gasAndHumidity', titleKey: 'cma.cards.gas-and-humidity.title', icon: '/static/imgs/cma-icons/gas-and-humidity.svg', buildRows: buildDynamicRowsOf('gasAndHumidity'),
  },
  {
    id: 'temperatures', statusKey: 'temperatures', titleKey: 'cma.cards.temperatures.title', icon: '/static/imgs/cma-icons/temperatures.svg', buildRows: buildDynamicRowsOf('temperatures'),
  },
  {
    id: 'load-voltage', statusKey: 'loadVoltage', titleKey: 'cma.cards.load-voltage.title', icon: '/static/imgs/cma-icons/load-voltage.svg', buildRows: buildDynamicRowsOf('loadVoltage'),
  },
  {
    id: 'bag', statusKey: 'bag', titleKey: 'cma.cards.bag.title', icon: '/static/imgs/cma-icons/bag.svg', buildRows: buildDynamicRowsOf('bag'),
  },
  {
    id: 'commutator', statusKey: 'commutator', titleKey: 'cma.cards.commutator.title', icon: '/static/imgs/cma-icons/commutator.svg', buildRows: buildDynamicRowsOf('commutator'),
  },
  {
    id: 'electrical-data', statusKey: 'electricalData', titleKey: 'cma.cards.electrical-data.title', icon: '/static/imgs/cma-icons/electrical-data.svg', buildRows: buildDynamicRowsOf('electricalData'),
  },
  {
    id: 'cooling', statusKey: 'cooling', titleKey: 'cma.cards.cooling.title', icon: '/static/imgs/cma-icons/cooling.svg', buildRows: buildCoolingRows,
  },
];

// Qual leitura preenche cada posição — por padrão a sua própria (não listada
// aqui), exceto estas 4, remapeadas por pedido: a posição do comutador
// mostra tensão na carga, a de gás/umidade mostra o comutador, e as
// posições de temperaturas (card 3) e tensão na carga (card 8) trocam entre
// si — o 3 mostra gás/umidade e o 8 mostra temperaturas. O pedido é sempre
// por conteúdo ("as informações do 3 deveriam estar no 8"); a numeração
// (índice+1 em CARD_BUILDERS) continua vindo só da posição, então não muda
// se este mapa mudar.
const CONTENT_OVERRIDES = {
  commutator: 'load-voltage',
  temperatures: 'gas-and-humidity',
  'load-voltage': 'temperatures',
  'gas-and-humidity': 'commutator',
};

/**
 * Mapeia as leituras (reais ou mockadas) para os cards com dado disponível
 * — um módulo sem card Cma (ver backend, decisão "sem dado → omitido") não
 * aparece aqui. O número (badge "N. Título") é sempre o da posição fixa na
 * lista de 7, mesmo quando algum card anterior está ausente — não
 * renumera.
 * @param {Object} readings Objeto no formato retornado por cmaDashboardService.getReadings.
 * @returns {Array} Lista de view-models de card, na ordem fixa da tela.
 */
const BUILDER_BY_ID = CARD_BUILDERS.reduce(
  (acc, builder) => ({ ...acc, [builder.id]: builder }),
  {},
);

// Número de linhas do skeleton de cada card, por conteúdo (contentId) — usado
// só durante o carregamento (ver CmaReadingCard.vue, prop `loading`), antes
// de saber quantas linhas os dados reais vão ter, para o card não mudar de
// altura quando a leitura chega. Espelha o número de linhas que cada
// build*Rows acima produz depois de agrupado (ver CmaReadingCard.vue,
// displayRows) — não o total bruto de campos.
const SKELETON_ROW_COUNTS_BY_CONTENT_ID = {
  bushings: 3, // 1 linha por fase (agrupada, ver groupRowsByPhase)
  'gas-and-humidity': 2,
  temperatures: 4, // óleo, enrolamentos (agrupados), ambiente, carregamento
  'load-voltage': 1,
  bag: 1,
  commutator: 2, // tap + tensão do motor (agrupada)
  'electrical-data': 4,
  cooling: 2,
};

/**
 * Mapeia as 8 posições fixas da tela para view-models de card "vazios"
 * (mesmos id/contentId/número/título/ícone de buildCards, sem status nem
 * leituras) — usados como skeleton enquanto a leitura ainda não chegou (ver
 * CmaDashboard.vue, computed `cards`). Ao contrário de buildCards, nunca
 * omite uma posição: não há como saber ainda quais módulos o ativo tem.
 * @returns {Array} Lista de view-models de card "vazios", na ordem fixa da tela.
 */
export function buildSkeletonCards() {
  return CARD_BUILDERS.map(({ id }, index) => {
    const contentId = CONTENT_OVERRIDES[id] || id;
    const content = BUILDER_BY_ID[contentId];
    return {
      id,
      contentId,
      number: index + 1,
      titleKey: content.titleKey,
      icon: content.icon,
      skeletonRows: SKELETON_ROW_COUNTS_BY_CONTENT_ID[contentId] || 3,
    };
  });
}

/**
 * As 8 posições saem SEMPRE, mesmo sem o módulo no banco: quem não tem dado
 * vem marcado com `unavailable`, e o card se apresenta apagado, sem badge de
 * status e sem linhas (ver CmaReadingCard.vue, `--unavailable`). Antes essas
 * posições eram filtradas fora e o card sumia da tela — a ausência ficava
 * indistinguível de um card que nunca existiu, e a grade dançava a cada
 * leitura. Aparecer discreto informa o que falta sem concorrer com o dado real.
 */
export function buildCards(readings) {
  return CARD_BUILDERS.map(({ id }, index) => {
    // A posição (`id`) e o conteúdo exibido nela (`contentId`) podem
    // divergir (ver CONTENT_OVERRIDES) — título, ícone, status e leituras
    // vêm sempre do CONTEÚDO, só `id`/`number` são da posição física.
    const contentId = CONTENT_OVERRIDES[id] || id;
    const content = BUILDER_BY_ID[contentId];
    const leitura = readings[content.statusKey];
    const unavailable = leitura === null || leitura === undefined;

    return {
      id,
      contentId,
      number: index + 1,
      // `title`: nome do card como o backend manda (ver cmaCardsAdapter.js,
      // já internacionalizado por lá) — só existe quando o módulo tem dado
      // (`!unavailable`). `titleKey` continua como fallback estático (ver
      // CmaReadingCard.vue) para quando não há: card ainda sem leitura
      // (`unavailable`) ou sem fonte no backend (ex.: Resfriamento, ver
      // buildCoolingRows).
      title: unavailable ? null : leitura.title,
      titleKey: content.titleKey,
      icon: content.icon,
      unavailable,
      status: unavailable ? null : readings.cardStatuses[content.statusKey],
      rows: unavailable ? [] : content.buildRows(readings),
    };
  });
}

/**
 * Mapeia as leituras para os 4 indicadores fixos da barra de resumo.
 * @param {Object} readings Objeto no formato retornado por cmaDashboardService.getReadings.
 * @returns {Array} Lista de view-models de indicador, na ordem fixa da barra.
 */
export function buildSummaryItems(readings) {
  return [
    {
      id: 'general-status',
      labelKey: 'cma.summary.general-status',
      icon: 'fa-shield-alt',
      value: readings.summary.generalStatus,
    },
    {
      id: 'asset-condition',
      labelKey: 'cma.summary.asset-condition',
      icon: 'fa-heartbeat',
      value: readings.summary.assetCondition,
    },
    {
      id: 'next-maintenance',
      labelKey: 'cma.summary.next-maintenance',
      icon: 'fa-calendar-alt',
      value: readings.summary.nextMaintenanceDate,
    },
    {
      id: 'operating-time',
      labelKey: 'cma.summary.operating-time',
      icon: 'fa-clock',
      value: readings.summary.operatingDays,
    },
  ];
}
